import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useUsers } from '@/hooks/use-api'
import { GRACKLE_COLORS } from '@/lib/theme'

interface BulkAssignModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedCount: number
  entityType: string
  onConfirm: (ownerId: string, notifyOwner: boolean) => void
  isAssigning?: boolean
}

export function BulkAssignModal({
  open,
  onOpenChange,
  selectedCount,
  entityType,
  onConfirm,
  isAssigning = false,
}: BulkAssignModalProps) {
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>('')
  const [notifyOwner, setNotifyOwner] = useState(true)
  const { data: usersData, isLoading: isLoadingUsers } = useUsers({
    filter: 'active',
    limit: 10000,
  }) // Active users only for the owner dropdown

  const users = usersData?.data ?? []

  // Reset selection when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedOwnerId('')
      setNotifyOwner(true)
    }
  }, [open])

  const handleConfirm = () => {
    if (selectedOwnerId) {
      onConfirm(selectedOwnerId, notifyOwner)
    }
  }

  const handleCancel = () => {
    setSelectedOwnerId('')
    setNotifyOwner(true)
    onOpenChange(false)
  }

  const selectedUser = users.find((u) => u.id === selectedOwnerId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]" aria-describedby="bulk-assign-description">
        <DialogHeader>
          <DialogTitle>Assign {entityType}s</DialogTitle>
          <DialogDescription id="bulk-assign-description">
            Assign {selectedCount} selected {entityType}
            {selectedCount === 1 ? '' : 's'} to a new owner.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="owner-select">New Owner</Label>
            {isLoadingUsers ? (
              <div className="text-sm text-muted-foreground">Loading users...</div>
            ) : (
              <Select
                value={selectedOwnerId}
                onValueChange={setSelectedOwnerId}
                disabled={isAssigning}
              >
                <SelectTrigger id="owner-select" aria-label="Select new owner for bulk assignment">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {[...users]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} {user.email ? `(${user.email})` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {selectedUser && (
            <div className="rounded-md bg-muted p-3">
              <p className="text-sm">
                <span className="font-medium">Selected owner:</span> {selectedUser.name}
                {selectedUser.email && (
                  <span className="text-muted-foreground"> ({selectedUser.email})</span>
                )}
              </p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="notify-owner"
              checked={notifyOwner}
              onCheckedChange={(checked) => setNotifyOwner(checked === true)}
              disabled={isAssigning}
            />
            <Label htmlFor="notify-owner" className="text-sm font-normal cursor-pointer">
              Notify new owner by email
            </Label>
          </div>

          {selectedCount > 10 && (
            <div
              className="rounded-md border p-3"
              style={{
                backgroundColor: GRACKLE_COLORS.pollen,
                borderColor: GRACKLE_COLORS.pollen,
                color: GRACKLE_COLORS.prussianBlue,
              }}
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm">
                You are about to reassign {selectedCount} {entityType}
                {selectedCount === 1 ? '' : 's'}. This action will update ownership for all selected
                items.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isAssigning}
            aria-label="Cancel bulk assignment"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedOwnerId || isAssigning}
            aria-label={`Confirm assignment of ${selectedCount} ${entityType}${selectedCount === 1 ? '' : 's'}`}
          >
            {isAssigning
              ? 'Assigning...'
              : `Assign ${selectedCount} ${entityType}${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
